import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--r-md)] text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--blue)] focus-visible:outline-offset-2 active:opacity-75 disabled:cursor-not-allowed disabled:opacity-90',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--ink)] text-[var(--bg)] border border-[var(--ink)] hover:opacity-90',
        accent: 'bg-[var(--accent)] text-[var(--accent-fg)] border border-[var(--accent)] hover:opacity-90',
        secondary: 'bg-[var(--surface-2)] text-[var(--text)] border border-[var(--border)] hover:bg-[var(--border)]',
        ghost: 'bg-transparent text-[var(--text)] border border-transparent hover:bg-[var(--surface-2)]',
        outline: 'border border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-2)]',
        danger: 'bg-[var(--danger)] text-[var(--danger-fg)] border border-[var(--danger)] hover:opacity-90',
      },
      size: {
        sm: 'h-11 px-3 text-xs',
        md: 'h-11 px-4',
        lg: 'h-12 px-5 text-sm',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});
Button.displayName = 'Button';
